import React, { useState, useRef, useEffect } from "react";

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
  contentClassName?: string;
  delay?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "top",
  className = "",
  contentClassName = "",
  delay = 300,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      updatePosition();
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  const updatePosition = () => {
    if (!childRef.current || !tooltipRef.current) return;

    const childRect = childRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let x = 0;
    let y = 0;

    switch (position) {
      case "top":
        x = childRect.left + childRect.width / 2 - tooltipRect.width / 2;
        y = childRect.top - tooltipRect.height - 8;
        break;
      case "bottom":
        x = childRect.left + childRect.width / 2 - tooltipRect.width / 2;
        y = childRect.bottom + 8;
        break;
      case "left":
        x = childRect.left - tooltipRect.width - 8;
        y = childRect.top + childRect.height / 2 - tooltipRect.height / 2;
        break;
      case "right":
        x = childRect.right + 8;
        y = childRect.top + childRect.height / 2 - tooltipRect.height / 2;
        break;
    }

    // Adjust for scroll
    x += window.scrollX;
    y += window.scrollY;

    // Ensure tooltip stays within viewport
    const padding = 10;
    if (x < padding) x = padding;
    if (y < padding) y = padding;
    if (x + tooltipRect.width > window.innerWidth - padding) {
      x = window.innerWidth - tooltipRect.width - padding;
    }
    if (y + tooltipRect.height > window.innerHeight - padding) {
      y = window.innerHeight - tooltipRect.height - padding;
    }

    setCoords({ x, y });
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
    }

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isVisible]);

  // Get arrow positioning styles based on tooltip position
  const getArrowStyles = () => {
    switch (position) {
      case "top":
        return {
          bottom: "-4px",
          left: "50%",
          transform: "translateX(-50%) rotate(45deg)",
          borderTop: "none",
          borderLeft: "none",
        };
      case "bottom":
        return {
          top: "-4px",
          left: "50%",
          transform: "translateX(-50%) rotate(45deg)",
          borderBottom: "none",
          borderRight: "none",
        };
      case "left":
        return {
          right: "-4px",
          top: "50%",
          transform: "translateY(-50%) rotate(45deg)",
          borderTop: "none",
          borderRight: "none",
        };
      case "right":
        return {
          left: "-4px",
          top: "50%",
          transform: "translateY(-50%) rotate(45deg)",
          borderBottom: "none",
          borderLeft: "none",
        };
      default:
        return {};
    }
  };

  return (
    <>
      <div
        ref={childRef}
        className={`inline-block ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`fixed z-50 h-[28px] px-2 py-1.5 text-xs text-[#1D2641] font-[700] bg-white rounded-[4px] shadow-md ${contentClassName}`}
          style={{ left: `${coords.x}px`, top: `${coords.y}px` }}
        >
          {content}
          <div className="absolute w-2 h-2 bg-white" style={getArrowStyles()} />
        </div>
      )}
    </>
  );
};

export default Tooltip;
